/**
 * Which category sections are collapsed, remembered across sessions.
 *
 * Sections default to **collapsed on a small screen and expanded on a large
 * one**: on a phone, six expanded categories mean the master list is a
 * scroll-and-a-half away, and the point of the dashboard is that the top of it
 * is the answer.
 *
 * Only explicit choices are stored. That way the responsive default keeps
 * applying to sections the user has never touched, including new ones.
 */

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'docket.collapsedCategories'
const SMALL_SCREEN = '(max-width: 767px)'

function readStored(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, boolean>)
      : {}
  } catch {
    // Private browsing, a quota error, or hand-edited junk. A forgotten
    // preference is not worth breaking the page over.
    return {}
  }
}

export function useCollapsed() {
  const [explicit, setExplicit] = useState<Record<string, boolean>>(readStored)
  const [smallScreen, setSmallScreen] = useState(
    () => window.matchMedia(SMALL_SCREEN).matches,
  )

  useEffect(() => {
    const query = window.matchMedia(SMALL_SCREEN)
    const onChange = (event: MediaQueryListEvent) => setSmallScreen(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(explicit))
    } catch {
      // Nothing to do; the preference is simply not remembered.
    }
  }, [explicit])

  const isCollapsed = useCallback(
    (id: number) => explicit[String(id)] ?? smallScreen,
    [explicit, smallScreen],
  )

  const toggle = useCallback(
    (id: number) =>
      setExplicit((current) => ({
        ...current,
        [String(id)]: !(current[String(id)] ?? smallScreen),
      })),
    [smallScreen],
  )

  const setAll = useCallback(
    (ids: number[], collapsed: boolean) =>
      setExplicit((current) => ({
        ...current,
        ...Object.fromEntries(ids.map((id) => [String(id), collapsed])),
      })),
    [],
  )

  return { isCollapsed, toggle, setAll }
}
